import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";

import { providerReports } from "@/lib/provider-payments.functions";
import { bookingFilters, money, type BookingFilters } from "@/lib/provider-schemas";
import { downloadCsv } from "@/lib/csv-export";
import { ErrorBox, ProviderModuleShell, StatCard } from "@/components/admin/provider/ProviderModuleShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/admin/provider-reports")({
  head: () => ({
    meta: [
      { title: "Reportes de proveedores — Administración" },
      { name: "description", content: "Ventas, costos, utilidad y pagos por proveedor, canal y tour." },
      { property: "og:title", content: "Reportes de proveedores — Administración · Mostrando México" },
      { property: "og:description", content: "Administración privada de proveedores por empresa." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  errorComponent: ({ error }) => <ErrorBox error={error as Error} />,
  notFoundComponent: () => <ErrorBox error={new Error("No encontrado")} />,
  component: () => (
    <ProviderModuleShell
      title="Reportes"
      description="Venta, comisión, ingreso neto, costo, pagado, pendiente y utilidad agrupados por proveedor, canal y tour."
    >
      <Body />
    </ProviderModuleShell>
  ),
});

const groups = [
  { key: "byProvider", label: "Por proveedor" },
  { key: "byChannel", label: "Por canal" },
  { key: "byTour", label: "Por tour" },
] as const;

function Body() {
  const [filters, setFilters] = useState<BookingFilters>(bookingFilters.parse({ limit: 2000 }));
  const [group, setGroup] = useState<(typeof groups)[number]["key"]>("byProvider");
  const { data } = useSuspenseQuery(
    queryOptions({
      queryKey: ["admin", "provider-reports", filters],
      queryFn: () => providerReports({ data: filters }),
    }),
  );
  const rows = data[group];

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Reservas activas" value={data.totals.activeBookings} hint={`${data.totals.pax} pax`} />
        {data.totals.byCurrency.map((c) => (
          <StatCard
            key={c.currency}
            label={`Utilidad ${c.currency}`}
            value={money(c.profit, c.currency)}
            hint={`Venta ${money(c.sale, c.currency)} · Costo ${money(c.cost, c.currency)}`}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-card p-4">
        <div className="space-y-1">
          <Label className="text-xs">Desde</Label>
          <Input
            type="date"
            value={filters.dateFrom ?? ""}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value || null })}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Hasta</Label>
          <Input
            type="date"
            value={filters.dateTo ?? ""}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value || null })}
          />
        </div>
        <div className="flex gap-2">
          {groups.map((g) => (
            <Button
              key={g.key}
              size="sm"
              variant={group === g.key ? "default" : "outline"}
              onClick={() => setGroup(g.key)}
            >
              {g.label}
            </Button>
          ))}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            downloadCsv(
              `reporte-${group}`,
              [
                { key: "key", label: "Grupo" },
                { key: "bookings", label: "Reservas" },
                { key: "pax", label: "Pax" },
                { key: "sale", label: "Venta" },
                { key: "commission", label: "Comisión" },
                { key: "net", label: "Ingreso neto" },
                { key: "cost", label: "Costo" },
                { key: "paid", label: "Pagado" },
                { key: "pending", label: "Pendiente" },
                { key: "profit", label: "Utilidad" },
                { key: "currency", label: "Moneda" },
              ],
              rows as never,
            )
          }
        >
          Exportar CSV
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Grupo</th>
              <th className="px-3 py-2 text-right">Reservas</th>
              <th className="px-3 py-2 text-right">Pax</th>
              <th className="px-3 py-2 text-right">Venta</th>
              <th className="px-3 py-2 text-right">Comisión</th>
              <th className="px-3 py-2 text-right">Ingreso neto</th>
              <th className="px-3 py-2 text-right">Costo</th>
              <th className="px-3 py-2 text-right">Pagado</th>
              <th className="px-3 py-2 text-right">Pendiente</th>
              <th className="px-3 py-2 text-right">Utilidad</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t">
                <td className="px-3 py-2 font-medium">{r.key}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.bookings}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.pax}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.sale, r.currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.commission, r.currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.net, r.currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.cost, r.currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.paid, r.currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.pending, r.currency)}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                  {money(r.profit, r.currency)}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={10} className="px-3 py-10 text-center text-muted-foreground">
                  Sin datos en el periodo seleccionado.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
